import { useAuth0 } from '@auth0/auth0-react';
import { Box, FormControl, InputLabel, MenuItem, Select } from '@mui/material';
import { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import SearchWidget from '../../../components/molecules/searchWidget';
import TableWidget, { TableColumn } from '../../../components/molecules/tableWidget';
import { TimeAgo } from '../../../components/timeAgo';
import { Spacing } from '../../../constants/theme';
import { Vehicle } from '../../../types/vehicle';
import Page from '../../../components/atoms/page';
import { fetchCheckedInVehicles } from '../../../api/vehicles';
import { useSelectedWorkshop } from '../../../context/workshopContext';
import DirectionsCarFilledIcon from '@mui/icons-material/DirectionsCarFilled';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import { ProcessStatusProgress } from '../../../components/atoms/processStatusProgress';

type VehicleListStatus = 'checkedIn' | 'checkedOut' | 'all';

type VehicleTableRow = {
  id: string;
  vin: string;
  vehicleMake: string;
  vehicleModel: string;
  vehicleYear: number | string;
  area: string;
  keyNumber: string;
  checkedInUtc: string;
  isRelocating: boolean;
  relocationInfo?: string | null;
  needAttention: boolean;
  attentionInfo?: string | null;
  progress?: number,
  updatedAtUtc?: string,
  vehicle: Vehicle;
};

const Vehicles = () => {
  const { isLoading } = useAuth0();
  const { selectedOrganisationId: workshopId, isLoading: isLoadingWorkshops } = useSelectedWorkshop()
  const [vehicles, setVehicles] = useState<Vehicle[]>()
  const [search, setSearch] = useState('')
  const [listStatus, setListStatus] = useState<VehicleListStatus>('checkedIn')
  const [isLoadingVehicles, setIsLoadingVehicles] = useState(true)

  const navigate = useNavigate()

  useEffect(() => {
    if (!workshopId) {
      if (!isLoadingWorkshops) {
        setIsLoadingVehicles(false)
      }
      return;
    }

    const fetchVehicles = async () => {
      setIsLoadingVehicles(true)
      try {
        const data = await fetchCheckedInVehicles(workshopId)
        setVehicles(data)
      } catch (error) {
        console.log('error with fetching vehicles', error)
      } finally {
        setIsLoadingVehicles(false)
      }
    }
    fetchVehicles()
  }, [workshopId, isLoadingWorkshops])

  const tableData = useMemo<VehicleTableRow[]>(() =>
    (vehicles ?? [])
      .filter(item => item.keyNumber?.toLowerCase().includes(search.toLowerCase()))
      .map((vehicle) => ({
        id: vehicle.vin,
        vin: vehicle.vin,
        vehicleMake: vehicle.vehicleMake,
        vehicleModel: vehicle.vehicleModel,
        vehicleYear: vehicle.vehicleYear,
        area: vehicle.currentLocation ?? vehicle.area ?? '',
        keyNumber: vehicle.keyNumber ?? '',
        checkedInUtc: vehicle.checkedInUtc,
        isRelocating: vehicle.isRelocating,
        relocationInfo: vehicle.relocationInfo,
        needAttention: vehicle.needAttention,
        attentionInfo: vehicle.attentionInfo,
        progress: vehicle.progress,
        updatedAtUtc: vehicle.progressUpdatedAtUtc,
        vehicle,
      })),
    [vehicles, search])

  const columns = useMemo<TableColumn[]>(() => [
    {
      id: 'isRelocating',
      label: '',
      width: 72,
      isSortable: false,
      format: (_value, row) => (
        <>
          {row.isRelocating === true ? (
            <DirectionsCarFilledIcon
              aria-label="Vehicle is relocating"
              titleAccess={typeof row.relocationInfo === 'string' ? row.relocationInfo : 'Vehicle is relocating'}
              color="success"
              fontSize="small"
            />
          ) : null}
          {row.needAttention === true ? (
            <ReportProblemOutlinedIcon
              aria-label="Vehicle needs attention"
              titleAccess={typeof row.attentionInfo === 'string' ? row.attentionInfo : 'Vehicle needs attention'}
              color="warning"
              fontSize="small"
            />
          ) : null}
        </>
      ),
    },
    { id: 'area', label: 'Location' },
    { id: 'keyNumber', label: 'Key number' },
    { id: 'vin', label: 'VIN' },
    { id: 'vehicleMake', label: 'Make' },
    { id: 'vehicleModel', label: 'Model' },
    { id: 'vehicleYear', label: 'Year' },
    {
      id: 'checkedInUtc',
      label: 'Checked in',
      format: (value) => <TimeAgo date={String(value)} />,
    },
    {
      id: 'progress',
      label: 'Process status',
      align: 'left',
      width: 180,
      format: (value, row) => (
        <ProcessStatusProgress
          value={typeof value === 'number' ? value : null}
          lastUpdated={
            typeof row.updatedAtUtc === 'string'
              ? row.updatedAtUtc
              : null
          }
        />
      ),
    },
  ], [])

  if (isLoading) {
    return <Box>Loading ...</Box>;
  }

  return (
    <>




      <Page title="Vehicles">
        <>
          <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'flex-end', gap: Spacing.gap, mb: Spacing.gap }}>

            <SearchWidget
              placeholder="Search keynumber..."
              initialValue={search}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
            />
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={listStatus}
                label="Status"
                onChange={(e) => setListStatus(e.target.value as VehicleListStatus)}
              >
                <MenuItem value="checkedIn">Checked in</MenuItem>
                <MenuItem value="checkedOut" disabled>Check out</MenuItem>
                <MenuItem value="all" disabled>All</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <TableWidget
            data={tableData}
            columns={columns}
            defaultSort="vehicleMake"
            onClickRow={(_event, _id, row) =>
              navigate(`/vehicle/${(row as VehicleTableRow).vehicle.vin}/${(row as VehicleTableRow).vehicle.workshopSessionId}`)
            }
            themes={{ device: 'desktop' }}
            pagination
            loadingStatus={isLoadingVehicles ? 'loading' : undefined}
          />
        </>

      </Page>
    </>
  );
};

export default Vehicles;
